export type EnterpriseRoadmapPhase = {
  id: string;
  wave: number;
  phaseNumber: number;
  title: string;
  summary: string;
  publicTitle: string;
  publicValue: string;
};

export const ENTERPRISE_ROADMAP: Array<EnterpriseRoadmapPhase> = [
  {
    id: 'phase-1',
    wave: 1,
    phaseNumber: 1,
    title: 'Foundation & Integrity',
    summary: 'Harden org setup, domain controls, and baseline analytics integrity for trusted reporting.',
    publicTitle: 'Phase 1: Data foundation',
    publicValue: 'Create a reliable enterprise data layer for adoption, trend movement, and cohort health reporting.',
  },
  {
    id: 'phase-2',
    wave: 2,
    phaseNumber: 2,
    title: 'Executive Visibility',
    summary: 'Launch leadership dashboards with cohort deltas, risk segmentation, and intervention recommendations.',
    publicTitle: 'Phase 2: Multi-tenant architecture',
    publicValue: 'Isolate organizations cleanly so enterprise clients can scale with secure workspace-level boundaries.',
  },
  {
    id: 'phase-3',
    wave: 3,
    phaseNumber: 3,
    title: 'Intelligent Activation',
    summary: 'Automate seat nudges, renewal triggers, and partner workflows based on engagement signal shifts.',
    publicTitle: 'Phase 3: Compliance hardening',
    publicValue: 'Strengthen controls, auditability, and operational runbooks to accelerate security and procurement approvals.',
  },
  {
    id: 'phase-4',
    wave: 4,
    phaseNumber: 4,
    title: 'Outcome Attribution',
    summary: 'Map trend improvements to retention, absenteeism, and wellbeing utilization outcomes.',
    publicTitle: 'Phase 4: Organization analytics',
    publicValue: 'Deliver executive-level aggregate insights that inform interventions, utilization strategy, and retention planning.',
  },
  {
    id: 'phase-5',
    wave: 5,
    phaseNumber: 5,
    title: 'Identity and Enterprise Controls',
    summary: 'Expand SSO pathways, domain policy controls, and admin governance at enterprise scale.',
    publicTitle: 'Phase 5: Identity and enterprise controls',
    publicValue: 'Support SSO pathways, domain governance, and seat controls for low-friction enterprise onboarding.',
  },
  {
    id: 'phase-6',
    wave: 6,
    phaseNumber: 6,
    title: 'Platform and Partner Integration',
    summary: 'Connect benefits, HRIS, and partner systems with secure APIs and event-level operational controls.',
    publicTitle: 'Phase 6: Platform and partner integration',
    publicValue: 'Integrate with benefits, HRIS, and partner systems through APIs that preserve privacy boundaries.',
  },
  {
    id: 'phase-7',
    wave: 7,
    phaseNumber: 7,
    title: 'Ecosystem & scale',
    summary: 'Deeper marketplace connectors, carrier integrations, and multi-region posture for large deployments.',
    publicTitle: 'Phase 7: Ecosystem',
    publicValue: 'Expand beyond core integrations into a broader partner and distribution ecosystem.',
  },
  {
    id: 'phase-8',
    wave: 8,
    phaseNumber: 8,
    title: 'Continuous optimization',
    summary: 'Production telemetry, cost controls, and roadmap iteration with the largest enterprise customers.',
    publicTitle: 'Phase 8: Optimization',
    publicValue: 'Operational excellence and continuous improvement across the enterprise platform.',
  },
  {
    id: 'phase-9',
    wave: 9,
    phaseNumber: 9,
    title: 'Strategic account growth',
    summary:
      'Renewal readiness, expansion motion, and executive review artifacts grounded in Wave 4 outcomes and Wave 7–8 operating context.',
    publicTitle: 'Phase 9: Account growth',
    publicValue: 'Turn platform signals into renewal confidence, expansion seats, and customer-facing review packs.',
  },
  {
    id: 'phase-10',
    wave: 10,
    phaseNumber: 10,
    title: 'Workspace-led account intelligence',
    summary:
      'Surface renewal and expansion signals inside the enterprise workspace app for qualified roles, aligned with Command Center previews.',
    publicTitle: 'Phase 10: Workspace account intelligence',
    publicValue:
      'Give owner, admin, and analyst roles a read-only renewal snapshot where they already work — without opening the admin console.',
  },
  {
    id: 'phase-11',
    wave: 11,
    phaseNumber: 11,
    title: 'Operator continuity',
    summary:
      'Org-context navigation between the enterprise workspace and admin consoles so CS and tenant admins stop copy-pasting organization IDs.',
    publicTitle: 'Phase 11: Operator continuity',
    publicValue:
      'Deep links and shortcuts between workspace telemetry and Command Center runbooks, with the active org carried in the query string.',
  },
  {
    id: 'phase-12',
    wave: 12,
    phaseNumber: 12,
    title: 'Bidirectional org context',
    summary:
      'Command Center links into the enterprise workspace honor `?org=` so the active organization syncs for end-user surfaces.',
    publicTitle: 'Phase 12: Bidirectional org context',
    publicValue:
      'The same organization ID flows from admin loaders to workspace home and check-in without manual selection when the user is a member.',
  },
];

/** Active execution track — Phase 12 Bidirectional org context (Wave 12). */
export const CURRENT_ENTERPRISE_PHASE_ID = 'phase-12';

export const CURRENT_ENTERPRISE_PHASE =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === CURRENT_ENTERPRISE_PHASE_ID) ?? ENTERPRISE_ROADMAP[0];

export const CURRENT_ENTERPRISE_WAVE = CURRENT_ENTERPRISE_PHASE.wave;

/** Next published roadmap phase after the current one (null when current is the last entry). */
export const NEXT_ENTERPRISE_PHASE: EnterpriseRoadmapPhase | null = (() => {
  const i = ENTERPRISE_ROADMAP.findIndex((p) => p.id === CURRENT_ENTERPRISE_PHASE_ID);
  if (i < 0 || i >= ENTERPRISE_ROADMAP.length - 1) return null;
  return ENTERPRISE_ROADMAP[i + 1]!;
})();

/** Pinned phases — wave admin hub copy (does not follow current execution track). */
export const PINNED_ENTERPRISE_PHASE_WAVE_3 =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === 'phase-3') ?? ENTERPRISE_ROADMAP[2];

export const PINNED_ENTERPRISE_PHASE_WAVE_4 =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === 'phase-4') ?? ENTERPRISE_ROADMAP[3];

export const PINNED_ENTERPRISE_PHASE_WAVE_5 =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === 'phase-5') ?? ENTERPRISE_ROADMAP[4];

export const PINNED_ENTERPRISE_PHASE_WAVE_7 =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === 'phase-7') ?? ENTERPRISE_ROADMAP[6];

/** Pinned Phase 8 — Wave 8 admin hub copy (`/enterprise/admin/wave-8`). */
export const PINNED_ENTERPRISE_PHASE_WAVE_8 =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === 'phase-8') ?? ENTERPRISE_ROADMAP[7];

/** Pinned Phase 9 — Wave 9 admin hub copy (`/enterprise/admin/wave-9`); does not follow current execution track. */
export const PINNED_ENTERPRISE_PHASE_WAVE_9 =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === 'phase-9') ?? ENTERPRISE_ROADMAP[8];

/** Pinned Phase 10 — Wave 10 admin hub copy (`/enterprise/admin/wave-10`). */
export const PINNED_ENTERPRISE_PHASE_WAVE_10 =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === 'phase-10') ?? ENTERPRISE_ROADMAP[9];

/** Pinned Phase 11 — Wave 11 admin hub (`/enterprise/admin/wave-11`). */
export const PINNED_ENTERPRISE_PHASE_WAVE_11 =
  ENTERPRISE_ROADMAP.find((phase) => phase.id === 'phase-11') ?? ENTERPRISE_ROADMAP[10];

/** Completed Wave 6 track — pinned for the Wave 6 admin hub (does not change when the current phase advances). */
export const PHASE_6_WAVE_WORKSTREAMS: Array<{
  id: string;
  title: string;
  status: 'in_progress' | 'queued' | 'completed';
  outcome: string;
}> = [
  {
    id: 'partner-outbound-webhooks',
    title: 'Partner workflow webhooks',
    status: 'completed',
    outcome:
      'Shipped: HTTPS outbound webhook, optional HMAC signing, JSON preview, test POST — configured in Command Center tenant settings.',
  },
  {
    id: 'aggregate-outcome-pipelines',
    title: 'Aggregate outcome pipelines (HRIS)',
    status: 'completed',
    outcome:
      'Shipped: Wave 4 outcome bridge CSV/JSON ingest, KV snapshots, cron snapshot hook — operators use Wave 4 admin for bulk history.',
  },
  {
    id: 'integration-reliability',
    title: 'Integration reliability & observability',
    status: 'completed',
    outcome:
      'Shipped: retries, delivery log + CSV, partner webhook-deliveries GET; dead-letter queue (last 50), admin replay + audit, partner webhook-dead-letters GET, Wave 6 tables.',
  },
  {
    id: 'partner-sla-hardening',
    title: 'Partner SLA & escalation',
    status: 'completed',
    outcome:
      'Shipped: SLA snapshot + Wave 6 card; tenant ops alert webhook (signed) + optional Resend email + min success %; 24h cooldown; cron POST …/cron/partner-slo-alerts (X-Cron-Secret).',
  },
];

/** Completed Wave 7 execution — pinned for `/enterprise/admin/wave-7`. */
export const WAVE_7_EXECUTION_WORKSTREAMS: Array<{
  id: string;
  title: string;
  status: 'in_progress' | 'queued' | 'completed';
  outcome: string;
}> = [
  {
    id: 'marketplace-connector-catalog',
    title: 'Marketplace connector catalog',
    status: 'completed',
    outcome:
      'Shipped: curated connector catalog (categories + descriptions), per-org rollout posture (none / evaluating / connected) in KV (`enterprise_marketplace_selections`), admin GET/POST marketplace-selections, Wave 7 table.',
  },
  {
    id: 'carrier-benefits-pathways',
    title: 'Carrier & benefits data pathways',
    status: 'completed',
    outcome:
      'Shipped: per-org carrier pathway profile in KV (`enterprise_carrier_pathways`) — program windows (eligibility / enrollment), integration channel checklist, CS + optimization notes; admin GET/POST carrier-pathways; Wave 7 form.',
  },
  {
    id: 'multi-region-ops-posture',
    title: 'Multi-region deployment posture',
    status: 'completed',
    outcome:
      'Shipped: per-org region posture in KV (`enterprise_region_posture`) — primary region, data residency notes, failover runbook, DR contact; admin GET/POST region-posture; Wave 7 form.',
  },
];

/** Phase 8 — Continuous optimization (hub: `/enterprise/admin/wave-8`). */
export const PHASE_8_OPTIMIZATION_WORKSTREAMS: Array<{
  id: string;
  title: string;
  status: 'in_progress' | 'queued' | 'completed';
  outcome: string;
}> = [
  {
    id: 'prod-telemetry-baselines',
    title: 'Production telemetry baselines',
    status: 'completed',
    outcome:
      'Shipped: per-org SLO targets in KV (`enterprise_telemetry_baselines`) — Edge p95 ms, partner webhook success %, admin error-budget notes, review cadence, alert routing; admin GET/POST telemetry-baselines; Wave 8 form.',
  },
  {
    id: 'cost-quota-guardrails',
    title: 'Cost & quota guardrails',
    status: 'completed',
    outcome:
      'Shipped: per-org caps & notes in KV (`enterprise_cost_quota_guardrails`) — monthly spend ceiling (USD), API RPM cap, storage GB cap, usage visibility, rate-limit posture, contract spend controls; admin GET/POST cost-quota-guardrails; Wave 8 form.',
  },
  {
    id: 'enterprise-feedback-loop',
    title: 'Largest-customer iteration loop',
    status: 'completed',
    outcome:
      'Shipped: Wave 7 carrier optimization notes + regional posture fields remain the CS intake surface; Wave 8 telemetry and cost guardrails capture operating context for downstream account planning.',
  },
];

/** Phase 9 — Strategic account growth (hub: `/enterprise/admin/wave-9`). */
export const PHASE_9_ACCOUNT_GROWTH_WORKSTREAMS: Array<{
  id: string;
  title: string;
  status: 'in_progress' | 'queued' | 'completed';
  outcome: string;
}> = [
  {
    id: 'account-growth-priorities-kv',
    title: 'Strategic account priorities (KV)',
    status: 'completed',
    outcome:
      'Shipped: per-org growth backlog in KV (`enterprise_account_growth_priorities`) — renewal narrative, expansion targets, QBR cadence, cross-functional owners; admin GET/POST account-growth-priorities; Wave 9 hub.',
  },
  {
    id: 'renewal-signal-alignment',
    title: 'Renewal & expansion signal alignment',
    status: 'completed',
    outcome:
      'Shipped: Wave 9 loads renewal & expansion preview in parallel with account priorities (same Edge API as Command Center); optional contract renewal date and utilization appear in the read-only summary next to narrative fields.',
  },
  {
    id: 'executive-review-artifacts',
    title: 'Executive review artifacts',
    status: 'completed',
    outcome:
      'Shipped: Wave 9 "Download Phase 9 pack" builds a QBR PDF via `downloadEnterprisePhase9ExecutivePackPdf` — Wave 4 outcome preview, Wave 8 telemetry & cost KV, renewal snapshot (when loaded), Phase 9 workstreams from wave-progress; Wave 1 readout remains on Command Center.',
  },
];

/** Phase 10 — Workspace account intelligence (hub: `/enterprise/admin/wave-10`). */
export const PHASE_10_WORKSPACE_ACCOUNT_INTELLIGENCE: Array<{
  id: string;
  title: string;
  status: 'in_progress' | 'queued' | 'completed';
  outcome: string;
}> = [
  {
    id: 'workspace-renewal-preview-api',
    title: 'Workspace renewal & expansion preview',
    status: 'completed',
    outcome:
      'Shipped: GET `/enterprise/workspace/renewal-preview` (owner/admin/analyst) reuses `computeRenewalExpansionSignals`; `fetchEnterpriseWorkspaceRenewalPreview` + workspace home card; Wave 10 admin hub.',
  },
  {
    id: 'workspace-qbr-distribution',
    title: 'Distribute QBR excerpts to workspace roles',
    status: 'completed',
    outcome:
      'Shipped: GET `/enterprise/workspace/qbr-excerpts` (owner/admin full KV; analyst/hr/manager renewal + QBR cadence); client flag `VITE_ENTERPRISE_QBR_WORKSPACE_EXCERPTS` / localStorage `tension.feature.enterpriseQbrWorkspaceExcerpts`; workspace home card when enabled.',
  },
];

/** Phase 11 — Operator continuity (hub: `/enterprise/admin/wave-11`). */
export const PHASE_11_OPERATOR_CONTINUITY: Array<{
  id: string;
  title: string;
  status: 'in_progress' | 'queued' | 'completed';
  outcome: string;
}> = [
  {
    id: 'workspace-admin-org-context-links',
    title: 'Workspace → admin org context links',
    status: 'completed',
    outcome:
      'Shipped: Workspace home "Admin shortcuts" opens Wave 8–10 and Command Center with `?org=` for the active organization (admin session still required).',
  },
  {
    id: 'wave-11-execution-hub',
    title: 'Wave 11 execution hub',
    status: 'completed',
    outcome:
      'Shipped: `/enterprise/admin/wave-11` summarizes Phase 11 and links to workspace + prior waves; sidebar entry under Enterprise.',
  },
];

/** Phase 12 — Bidirectional org context (hub: `/enterprise/admin/wave-12`). */
export const PHASE_12_BIDIRECTIONAL_ORG_CONTEXT: Array<{
  id: string;
  title: string;
  status: 'in_progress' | 'queued' | 'completed';
  outcome: string;
}> = [
  {
    id: 'enterprise-org-query-sync',
    title: 'Workspace respects `?org=` for active membership',
    status: 'completed',
    outcome:
      'Shipped: `EnterpriseOrgProvider` applies `org` query param after memberships load so deep links select the tenant.',
  },
  {
    id: 'command-center-workspace-deep-links',
    title: 'Command Center → workspace links carry org',
    status: 'completed',
    outcome:
      'Shipped: "Open workspace home (this org)" and enterprise check-in links append `?org=` from the active Command Center org field.',
  },
];

/** Per-org progress keys (`wave-progress`); IDs follow the active phase (Phase 12). */
export const CURRENT_PHASE_EXECUTION_WORKSTREAMS = PHASE_12_BIDIRECTIONAL_ORG_CONTEXT;
